'use client';

import React, { useCallback } from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';

import { Slider } from '@/types/slider';

import { DotButton, useDotButton } from './dot-button';

import './embla.css';

// Component for displaying slides
const SliderContent: React.FC<{ slides: Slider[] }> = ({ slides }) => {
    // const t = useTranslations();
    const [emblaRef, emblaApi] = useEmblaCarousel(
        {
            loop: true,
        },
        [Autoplay({ delay: 5000, stopOnInteraction: false })],
    );

    const onButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
        const autoplay = emblaApi.plugins().autoplay;
        if (!autoplay) return;
        autoplay.reset();
    }, []);

    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onButtonClick);

    return (
        <section className='embla'>
            <div className='relative top-40 flex w-full items-center justify-center lg:top-96 '>
                <div
                    id='main-slider-title'
                    dangerouslySetInnerHTML={{ __html: slides[selectedIndex].title }}
                    className='font-greatVibes absolute z-10 max-w-[240px] text-center text-2xl text-[38px] font-normal leading-[38px] text-white lg:max-w-[690px] lg:text-[110px] lg:leading-[110px]'
                />
                <Image
                    src='/svg/title-decoration.svg'
                    alt='title-decoration'
                    width={244}
                    height={70}
                    className='absolute -bottom-20 z-10 max-w-[90px] lg:-bottom-0 lg:top-40 lg:max-w-[244px]'
                />
            </div>
            <div className='embla__viewport' ref={emblaRef}>
                <div className='embla__container'>
                    {slides?.map(slide => (
                        <div className='embla__slide' key={slide.id}>
                            <div className='embla__slide__inner'>
                                <Image
                                    className='embla__slide__img'
                                    src={slide.image || '/images/404.jpg'}
                                    alt={`Slide ${slide.id}`}
                                    fill
                                    priority
                                    loading='eager'
                                    sizes='(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw'
                                    placeholder='blur'
                                    blurDataURL='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNzAwIiBoZWlnaHQ9IjQ3NSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiLz4='
                                />
                                <div className='embla__slide__overlay' />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {slides?.length > 1 && scrollSnaps.length > 0 && (
                <div className='embla__controls'>
                    <div className='embla__dots'>
                        {scrollSnaps.map((_, index) => (
                            <DotButton
                                key={`dot-${index}`}
                                onClick={() => onDotButtonClick(index)}
                                className={`embla__dot${index === selectedIndex ? ' embla__dot--selected' : ''}`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};
interface EmblaCarouselProps {
    slides: Slider[];
}
const EmblaCarousel: React.FC<EmblaCarouselProps> = ({ slides }) => {
    if (slides?.length === 0) {
        return null;
    }

    return <SliderContent slides={slides} />;
};

export default EmblaCarousel;
